import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Building2, MapPin, Wrench, ShieldCheck, FileSignature,
  CheckCircle2, XCircle, ExternalLink, Star,
} from "lucide-react";
import { AuthImage } from "@/components/AuthImage";
import { authFileUrl } from "@/lib/api";

export function ShopReviewSheet({ shopUserId, open, onOpenChange, onAction }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && shopUserId) {
      setLoading(true);
      api.get(`/admin/shops/${shopUserId}/application`).then((res) => setData(res.data)).finally(() => setLoading(false));
    } else {
      setData(null);
    }
  }, [open, shopUserId]);

  const shop = data?.shop;
  const app = data?.application;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl bg-[#09090B] border-l border-white/10 p-0 text-white overflow-y-auto">
        <SheetHeader className="px-6 py-5 border-b border-white/10 bg-[#121214] sticky top-0 z-10">
          <div className="label-eyebrow mb-1">SHOP APPLICATION REVIEW</div>
          <SheetTitle className="font-heading text-2xl">{app?.legal_business_name || shop?.shop_name || "Application"}</SheetTitle>
          {shop && <div className="text-xs text-zinc-500 font-mono">{shop.email}</div>}
        </SheetHeader>

        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-zinc-500">Loading application…</div>
          ) : !app ? (
            <div className="border border-[#FF9500]/40 bg-[#FF9500]/5 p-6">
              <div className="label-eyebrow text-[#FF9500] mb-2">AWAITING APPLICATION</div>
              <p className="text-sm text-zinc-300">This shop hasn't filled out the application form yet. They cannot be approved until they submit it.</p>
            </div>
          ) : (
            <>
              <ReviewSection icon={Building2} title="Business identity">
                <KV k="Legal name" v={app.legal_business_name} />
                <KV k="DBA" v={app.dba_name || "—"} />
                <KV k="Business type" v={app.business_type} />
                <KV k="Year founded" v={app.year_founded} />
                <KV k="Owner" v={`${app.owner_full_name} · ${app.owner_phone}`} />
                <KV k="Tax ID" v={app.tax_id} mono />
              </ReviewSection>

              <ReviewSection icon={MapPin} title="Location & service area">
                <KV k="Mode" v={app.is_mobile ? "Mobile shop" : "Storefront"} />
                {!app.is_mobile && (
                  <KV k="Address" v={`${app.address?.street}, ${app.address?.city}, ${app.address?.state} ${app.address?.zip}`} />
                )}
                <KV k="Service radius" v={`${app.service_radius_miles} mi`} />
                <div className="pt-2">
                  <div className="label-eyebrow text-zinc-500 mb-2">Photos ({app.shop_photos?.length || 0})</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(app.shop_photos || []).map((p) => (
                      <a key={p} href={authFileUrl(p)} target="_blank" rel="noopener noreferrer" className="block aspect-square bg-zinc-900 hover:opacity-80 transition" data-testid={`review-photo-${p}`}>
                        <AuthImage path={p} className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              </ReviewSection>

              <ReviewSection icon={Wrench} title="Capabilities">
                <div>
                  <div className="label-eyebrow text-zinc-500 mb-2">Service categories</div>
                  <div className="flex flex-wrap gap-1.5">
                    {(app.service_categories || []).map((c) => (
                      <span key={c} className="text-[10px] uppercase tracking-widest border border-white/15 px-2 py-1 text-zinc-300">{c}</span>
                    ))}
                  </div>
                </div>
                <KV k="Years experience" v={`${app.years_experience} yrs`} />
                <KV k="Warranty" v={app.warranty_offered} />
              </ReviewSection>

              <ReviewSection icon={ShieldCheck} title="License & insurance">
                <KV k="License #" v={`${app.license_state} · ${app.license_number}`} mono />
                <KV k="Insurance" v={`${app.insurance_carrier} · policy ${app.insurance_policy_number}`} mono />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                  <DocLink path={app.license_document} label="License document" />
                  <DocLink path={app.insurance_coi_document} label="COI document" />
                </div>
              </ReviewSection>

              <ReviewSection icon={FileSignature} title="Agreements">
                <div className="space-y-1.5">
                  {[
                    ["tos", "Terms of Service"],
                    ["privacy", "Privacy policy"],
                    ["anonymity", "Anti-doxxing / Anonymity"],
                    ["pricing_transparency", "Pricing transparency"],
                    ["dispute_resolution", "Dispute resolution"],
                  ].map(([k, label]) => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      {app.agreements?.[k] ? <CheckCircle2 className="w-4 h-4 text-[#34C759]" /> : <XCircle className="w-4 h-4 text-[#FF3B30]" />}
                      <span className="text-zinc-300">{label}</span>
                    </div>
                  ))}
                </div>
              </ReviewSection>
            </>
          )}
        </div>

        {app && (
          <div className="sticky bottom-0 bg-[#121214] border-t border-white/10 p-4 flex gap-3 z-10">
            <button onClick={() => onAction("reject", shop)} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="review-reject-btn">
              <XCircle className="w-4 h-4" /> Reject
            </button>
            <button onClick={() => onAction("approve", shop)} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="review-approve-btn">
              <CheckCircle2 className="w-4 h-4" /> Approve
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ReviewSection({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <Icon className="w-4 h-4 text-[#FF3B30]" />
        <h3 className="label-eyebrow text-white">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function KV({ k, v, mono }) {
  return (
    <div className="grid grid-cols-3 gap-3 text-sm">
      <div className="label-eyebrow text-zinc-500">{k}</div>
      <div className={`col-span-2 ${mono ? "font-mono text-xs" : ""}`}>{v ?? "—"}</div>
    </div>
  );
}

function DocLink({ path, label }) {
  if (!path) return <div className="text-xs text-zinc-500">{label}: not uploaded</div>;
  return (
    <a href={authFileUrl(path)} target="_blank" rel="noopener noreferrer" className="border border-white/15 bg-[#09090B] p-3 hover:border-white/30 flex items-center justify-between text-sm" data-testid={`doc-link-${label}`}>
      <span className="text-zinc-300">{label}</span>
      <ExternalLink className="w-4 h-4 text-zinc-500" />
    </a>
  );
}
